import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

/**
 * InfoModal - Componente default para modais de informação
 *
 * Baseado no padrão usado nas ofertas GDBN com Dialog, DialogTitle, DialogContent e DialogActions.
 * Use este componente para exibir informação ao utilizador com header e footer consistentes.
 *
 * @param {boolean} open - Controla se a modal está aberta
 * @param {function} onClose - Função chamada ao fechar a modal
 * @param {string} title - Título da modal (aparece no header)
 * @param {node} children - Conteúdo da modal
 * @param {string} maxWidth - Largura máxima da modal ("xs", "sm", "md", "lg", "xl")
 * @param {boolean} fullWidth - Se a modal deve ocupar toda a largura disponível
 * @param {string} closeButtonText - Texto do botão de fechar
 */
function InfoModal({
  open,
  onClose,
  title,
  children,
  maxWidth,
  fullWidth,
  closeButtonText,
  actions,
}) {
  const { t } = useTranslation(["common"]);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          maxHeight: "95vh",
          ...(isDark && {
            bgcolor: "#3a4556",
            backgroundImage: "none",
          }),
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: isDark ? "rgba(0, 0, 0, 0.75)" : "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.15rem",
          fontWeight: 600,
          pb: 2,
          ...(isDark && {
            borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
            color: "#ffffff",
          }),
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          minHeight: "200px",
          py: 2.5,
          ...(isDark && {
            borderColor: "rgba(255, 255, 255, 0.15)",
            color: "rgba(255, 255, 255, 0.9)",
            "& .MuiTypography-root": {
              color: "rgba(255, 255, 255, 0.9)",
            },
            "& .MuiCard-root": {
              bgcolor: "#2d3748",
              backgroundImage: "none",
              borderColor: "rgba(255, 255, 255, 0.1)",
            },
            "& .MuiDivider-root": {
              borderColor: "rgba(255, 255, 255, 0.15)",
            },
          }),
        }}
      >
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          ...(isDark && {
            borderTop: "1px solid rgba(255, 255, 255, 0.15)",
          }),
        }}
      >
        {actions || (
          <Button
            onClick={onClose}
            sx={isDark ? { color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff" } } : {}}
          >
            {closeButtonText || t("close")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

InfoModal.defaultProps = {
  maxWidth: "md",
  fullWidth: true,
  closeButtonText: null,
  actions: null,
};

InfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  fullWidth: PropTypes.bool,
  closeButtonText: PropTypes.string,
  actions: PropTypes.node,
};

export default InfoModal;
